import AppLayout from '../layouts/AppLayout'
import React, { Fragment, useEffect, useState, useContext, useMemo } from 'react'
import { AppContext } from '../contexts/AppContext'
import { Link, createMemoryRouter, useNavigate, useParams } from 'react-router-dom'
import { Dialog, Transition, Switch, Listbox } from '@headlessui/react'
import { RxCross2, RxChevronDown, RxPlusCircled } from 'react-icons/rx'
import Bounce from 'react-activity/dist/Bounce'
import 'react-activity/dist/Bounce.css'
import Spinner from 'react-activity/dist/Spinner'
import 'react-activity/dist/Spinner.css'
import { HiArrowNarrowRight, HiOutlinePhotograph } from 'react-icons/hi'
import { FaCheck } from 'react-icons/fa6'
import { FaTimes } from 'react-icons/fa'
import { getCompetitionImage } from '../utils/competition'
import { addMonths, format } from 'date-fns'

const DATE_FORMAT = 'yyyy-MM-dd'
const DEFAULT_START_DATE = format(new Date(), DATE_FORMAT)
const DEFAULT_END_DATE = format(addMonths(new Date(), 2), DATE_FORMAT)

const CreateCompetition = () => {
  const { createCompetition, isLoading, getCompetitions, updateCompetition } = useContext(AppContext)
  const { competitionId } = useParams()
  const [name, setName] = useState('')
  const [status, setStatus] = useState('Active')
  const [startDate, setStartDate] = useState(DEFAULT_START_DATE)
  const [endDate, setEndDate] = useState(DEFAULT_END_DATE)
  const [file, setFile] = useState(null)
  const [filePreviewURL, setFilePreviewURL] = useState('')
  const [error, setError] = useState('')

  const navigate = useNavigate()

  const handleFileUpload = (event) => {
    const [uploadedFile] = event.target.files
    setFile(uploadedFile)
    setFilePreviewURL(URL.createObjectURL(uploadedFile))
  }

  const onRemoveFile = () => {
    setFile(null)
    setFilePreviewURL('')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (isLoading) return

    try {
      setError('')
      let response = ''

      if (!name || !startDate || !endDate) {
        setError('All fields are required')
        return
      }

      if (startDate > endDate) {
        setError('Start date must be before end date')
        return
      }

      if (competitionId) {
        response = await updateCompetition({ name, status, file, competitionId, startDate, endDate })
      } else {
        response = await createCompetition({ name, status, file, startDate, endDate })
      }

      if (response === 'success') {
        navigate('/competitions')
      }
    } catch (error) {
      setError('Request failed: ' + error)
    }
  }

  const fetchCompetition = async () => {
    try {
      const response = await getCompetitions()
      const competition = response.find((c) => c.id === parseInt(competitionId))
      if (competition) {
        setName(competition.name)
        setStatus(competition.status)
        setStartDate(competition.startDate || DEFAULT_START_DATE)
        setEndDate(competition.endDate || DEFAULT_END_DATE)
        const hasLogo = competition.logo !== null && competition.logo !== undefined && competition.logo !== ''
        if (hasLogo) {
          setFilePreviewURL(getCompetitionImage(competition.logo))
        }
      }
    } catch (error) {
      setError('Error fetching category: ' + error)
    }
  }

  useEffect(() => {
    if (competitionId) {
      fetchCompetition()
    }
  }, [competitionId])

  return (
    <AppLayout pageName={`${competitionId ? 'Update' : 'Create'} Competition`}>
      <section>
        <form
          onSubmit={handleSubmit}
          method='post'
          encType='multipart/form-data'
          className='max-w-2xl'
        >
          <div className='mb-2'>
            <div className='p-2'>
              <label htmlFor='name'>Category Image (optional)</label>
              {!filePreviewURL ? (
                <label
                  htmlFor='logo'
                  className='flex flex-col items-center justify-center gap-2 w-[200px] h-[150px] rounded-xl border-2 border-dashed border-gray-300 text-gray-400 cursor-pointer hover:border-gray-400 hover:text-gray-500'
                >
                  <HiOutlinePhotograph className='w-10 h-10' />
                  <span className='text-sm'>Upload logo</span>
                  <input
                    id='logo'
                    name='logo'
                    type='file'
                    className='sr-only'
                    onChange={handleFileUpload}
                  />
                </label>
              ) : (
                <div className='w-[200px] h-[auto] relative'>
                  <div
                    onClick={onRemoveFile}
                    className='text-cancel absolute opacity-0 hover:opacity-50 w-full h-full bg-blue-200 flex justify-center items-center cursor-pointer'
                  >
                    remove
                  </div>
                  <img
                    src={filePreviewURL}
                    className='rounded-xl'
                    alt=''
                  />
                </div>
              )}
            </div>
            <div className='p-2'>
              <label htmlFor='name'>Category Name</label>
              <input
                id='name'
                name='name'
                required
                type='text'
                className='form-control'
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className='p-2 flex space-x-3'>
              <div className='flex-1'>
                <label htmlFor='startDate'>Start Date</label>
                <input
                  id='startDate'
                  name='startDate'
                  required
                  type='date'
                  className='form-control'
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className='flex-1'>
                <label htmlFor='endDate'>End Date</label>
                <input
                  id='endDate'
                  name='endDate'
                  required
                  type='date'
                  min={startDate}
                  className='form-control'
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>
            <div className='p-2'>
              <Listbox
                value={status}
                onChange={setStatus}
              >
                <Listbox.Label>Status</Listbox.Label>
                <div className='relative mt-1'>
                  <Listbox.Button className='form-control relative w-full cursor-default rounded-lg bg-white py-2 pl-3 pr-10 text-left focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300 sm:text-sm'>
                    <span className='block truncate'>{status}</span>
                    <span className='pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2'>
                      <RxChevronDown
                        className='h-5 w-5 text-gray-400'
                        aria-hidden='true'
                      />
                    </span>
                  </Listbox.Button>
                  <Transition
                    as={Fragment}
                    leave='transition ease-in duration-100'
                    leaveFrom='opacity-100'
                    leaveTo='opacity-0'
                  >
                    <Listbox.Options className='absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm z-10'>
                      {['Active', 'Inactive'].map((status) => (
                        <Listbox.Option
                          key={status}
                          className={({ active }) =>
                            `relative cursor-default select-none py-2 pl-10 pr-4 ${
                              active ? 'bg-amber-100 text-amber-900' : 'text-gray-900'
                            }`
                          }
                          value={status}
                        >
                          {status}
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  </Transition>
                </div>
              </Listbox>
            </div>

            <div className='px-2'>
              {error !== '' ? (
                <div className='text-left text-red-500 font-semibold text-md mb-2 tracking-wide'>{error}</div>
              ) : (
                <div className='py-4'></div>
              )}

              <div className='flex space-x-3'>
                <button
                  type='submit'
                  className='btn-submit'
                  disabled={isLoading}
                >
                  <span>{competitionId ? 'Save Changes' : 'Submit'}</span>
                  <span>
                    {isLoading === false ? (
                      <HiArrowNarrowRight />
                    ) : (
                      <Spinner
                        size={14}
                        className='ml-2'
                        animating={isLoading}
                      />
                    )}
                  </span>
                </button>
                {competitionId && (
                  <Link
                    to={'/competitions'}
                    className='btn-cancel'
                    disabled={isLoading}
                  >
                    <span>Cancel</span>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </form>
      </section>
    </AppLayout>
  )
}

export default CreateCompetition
